import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch, fetchAccount, getCachedAccount, steamLinkUrl, STEAM_LINK_RESULT } from '../api';
import {
  EXT_EVENT, EXT_READY_EVENT, requestExtensionStatus, requestSteamWhoami,
} from '../extensionEvents';

// Everything the Steam link flow can come back with, in words. `taken` is the
// only one where retrying will never help, so it names the way out instead.
const LINK_ERRORS = {
  taken: 'That Steam account is already connected to a different CS2 Profit Tracker account. '
       + 'Sign out and use "Sign in through Steam" to reach it.',
  already_linked: 'That is not the Steam account this profile is connected to. '
                + 'Sign out of Steam, sign back in as the account shown here, and try again.',
  ticket:  'That link request expired before it reached Steam. Please try again.',
  expired: 'The link took too long and expired. Please try again.',
  auth:    'Steam could not verify that sign-in. Please try again.',
  session: 'Your session expired while linking. Please sign in again.',
  server:  'Something went wrong finishing the link. Please try again.',
};

const fmtDate = (iso) => {
  if (!iso) return 'n/a';
  const d = new Date(iso);
  return Number.isNaN(+d) ? 'n/a' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const fmtWhen = (iso) => {
  if (!iso) return 'n/a';
  const d = new Date(iso);
  if (Number.isNaN(+d)) return 'n/a';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/**
 * Who you are, which Steam account you are, and whether the extension agrees.
 *
 * Split out of the Inventory tab, which was the wrong home for it: that tab is
 * about items, and the account behind them was competing with them for the top
 * of the page. Here the three identities the app juggles sit side by side,
 * which is what makes a disagreement between them legible.
 */
export default function ProfileTab({ user, rowCount = 0, inventoryCount = 0, zone = false }) {
  const [account, setAccount] = useState(() => getCachedAccount());
  const [extStatus, setExtStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(
    STEAM_LINK_RESULT.error ? (LINK_ERRORS[STEAM_LINK_RESULT.error] || LINK_ERRORS.server) : ''
  );
  const [justLinked] = useState(Boolean(STEAM_LINK_RESULT.linked));
  const [merged] = useState(Boolean(STEAM_LINK_RESULT.merged));

  useEffect(() => {
    let cancelled = false;
    fetchAccount()
      .then(a => { if (!cancelled && a) setAccount(a); })
      .catch(() => { /* keep the cached view */ });
    return () => { cancelled = true; };
  }, []);

  const [checking, setChecking] = useState(false);

  // A live check answers on the same event as an ordinary status update, so the
  // listener below handles both; this only drives the button's pending state.
  const runCheck = useCallback(() => {
    setChecking(true);
    requestSteamWhoami();
    // The extension may be gone or wedged, in which case no reply ever arrives.
    // Release the button rather than leaving it spinning forever.
    setTimeout(() => setChecking(false), 8000);
  }, []);

  useEffect(() => {
    const onReady = () => requestExtensionStatus();
    const onMessage = (event) => {
      if (event.source !== window) return;
      if (event.data?.type === EXT_EVENT.STATUS) {
        setExtStatus(event.data);
        setChecking(false);
      }
    };
    document.addEventListener(EXT_READY_EVENT, onReady);
    window.addEventListener('message', onMessage);
    requestExtensionStatus();
    const t = setTimeout(requestExtensionStatus, 800);
    return () => {
      document.removeEventListener(EXT_READY_EVENT, onReady);
      window.removeEventListener('message', onMessage);
      clearTimeout(t);
    };
  }, []);

  const startLink = useCallback(async () => {
    setError('');
    setStarting(true);
    try {
      window.location.href = await steamLinkUrl();
    } catch {
      setStarting(false);
      setError('Could not reach the server to start linking.');
    }
  }, []);

  const steamId   = account?.steamId || null;
  const verified  = account ? !account.needsSteamLink : false;
  const steamName = account?.steamName || null;

  // What the extension reports about the Steam account signed into THIS browser,
  // which is a different question from which account the tracker is bound to.
  const browserSteamId = extStatus?.browserSteamId || null;
  const mismatch = extStatus?.steamMismatch === true;
  // Tri-state: true, false, or null for "no live check has run". Only an
  // explicit false means signed out.
  const signedOut = extStatus?.browserLoggedIn === false;

  const steamStatus = verified ? 'Verified' : steamId ? 'Not confirmed' : 'Not connected';
  const extText = !extStatus ? 'Not detected'
    : !extStatus.connected ? 'Installed, not connected'
    : mismatch  ? 'Signed in as someone else'
    : signedOut ? 'Not signed into Steam'
    : 'Connected';
  const extTone = !extStatus?.connected ? 'warn'
    : (mismatch || signedOut) ? 'bad'
    : 'good';

  const rows = [
    ['Username',        user || account?.username || 'n/a'],
    ['Account created', fmtDate(account?.joinedAt)],
    ['Sign-in',         account?.hasPassword ? (steamId ? 'Password + Steam' : 'Password') : 'Steam'],
    ['Tracked items',   String(rowCount)],
    ['Inventory items', String(inventoryCount)],
  ];

  const steamRows = [
    ['Steam name',    steamName || 'n/a'],
    ['Steam ID',      steamId   || 'n/a'],
    ['Steam member since', fmtDate(account?.steamJoinedAt)],
  ];

  const extRows = [
    ['Signed into Steam as',
      signedOut ? 'Nobody, Steam is signed out'
        : browserSteamId || 'Unknown, run a check'],
    ['Last checked', fmtWhen(extStatus?.browserSteamIdAt)],
    ['Last sync',    fmtWhen(extStatus?.lastSync)],
  ];

  const Wrap = zone ? ZonePanel : ClassicPanel;

  return (
    <div style={{ padding: zone ? 0 : '20px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>

      <Wrap title="Account" badge={null}>
        <Facts rows={rows} zone={zone} />
      </Wrap>

      <Wrap title="Steam" badge={{ text: steamStatus, tone: verified ? 'good' : 'warn' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          {account?.avatarUrl ? (
            <img src={account.avatarUrl} alt="" width={48} height={48} style={{ borderRadius: 8, flexShrink: 0 }} />
          ) : (
            <div style={{ width: 48, height: 48, borderRadius: 8, flexShrink: 0, background: 'rgba(255,255,255,0.06)' }} />
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 600 }}>{steamName || 'No Steam account connected'}</div>
            <div style={{ fontFamily: 'monospace', fontSize: 14.5, color: 'var(--text-muted, #9ca3af)' }}>
              {steamId || 'n/a'}
            </div>
          </div>
        </div>

        <Facts rows={steamRows} zone={zone} />

        {justLinked && !error && (
          <Note zone={zone} tone="good">
            {steamName ? `Connected to ${steamName}.` : 'Steam account connected.'}
            {merged && ' Items already tracked against this Steam account by the browser extension '
                     + 'have been moved into this account.'}
          </Note>
        )}

        {error && <Note zone={zone} tone="bad">{error}</Note>}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
          {!verified && (
            <Action zone={zone} primary onClick={startLink} disabled={starting}>
              {starting ? 'Contacting Steam…' : steamId ? 'Confirm through Steam' : 'Sign in through Steam'}
            </Action>
          )}
          {steamId && (
            <Action zone={zone} href={`https://steamcommunity.com/profiles/${steamId}`}>
              View Steam profile
            </Action>
          )}
        </div>
      </Wrap>

      <PasswordSection
        zone={zone}
        hasPassword={account?.hasPassword === true}
        onChanged={(next) => setAccount(next)}
      />

      <Wrap title="Browser extension" badge={{ text: extText, tone: extTone }}>
        {mismatch && (
          <Note zone={zone} tone="bad">
            Steam is signed in as <span style={{ fontFamily: 'monospace' }}>{browserSteamId}</span>,
            which is not this account. Inventory syncs are blocked until you sign into Steam with
            the right account, or reconnect the extension.
          </Note>
        )}
        {/* The check ran and could not get an answer. Distinct from being
            signed out, and usually fixable by the user, so it says how. */}
        {extStatus?.ok === false && extStatus?.error && (
          <Note zone={zone} tone="warn">
            Could not confirm which Steam account is signed in: {extStatus.error}
          </Note>
        )}
        {signedOut && !mismatch && (
          <Note zone={zone} tone="bad">
            Steam is signed out in this browser, so nothing can sync. Sign in at
            steamcommunity.com and check again.
          </Note>
        )}
        {!extStatus?.connected && (
          <Note zone={zone} tone="muted">
            Connect the extension from the Inventory tab to sync your Steam inventory
            and trade history.
          </Note>
        )}
        <Facts rows={extRows} zone={zone} />

        {extStatus?.connected && (
          <div style={{ marginTop: 12 }}>
            {/* Costs a request to Steam, so it is a button rather than something
                that happens on every render of this tab. */}
            <Action zone={zone} disabled={checking} onClick={runCheck}>
              {checking ? 'Checking…' : 'Check Steam login now'}
            </Action>
          </div>
        )}
      </Wrap>

    </div>
  );
}

/**
 * Change the password, or set the first one.
 *
 * The current-password field is absent for accounts that do not have one, which
 * is every account created by signing in through Steam. Asking those users to
 * confirm a password they were never given would be a form nobody can submit;
 * the server applies the same rule, so this is presentation, not the check.
 *
 * The two new-password fields are compared here and nowhere else. The server has
 * no opinion about a value the user typed twice, only about what the password
 * ends up being.
 */
function PasswordSection({ zone, hasPassword, onChanged }) {
  const Wrap = zone ? ZonePanel : ClassicPanel;

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');

  // Mirrors userManager.validatePassword. Duplicated deliberately: this one
  // exists to answer before a round trip, the server's is the one that counts.
  const localProblem = () => {
    if (hasPassword && !current) return 'Enter your current password';
    if (next.length < 10)        return 'New password must be at least 10 characters';
    if (!/[A-Z]/.test(next))     return 'New password must include an uppercase letter';
    if (!/[0-9]/.test(next))     return 'New password must include a number';
    if (next !== confirm)        return 'The two new passwords do not match';
    if (hasPassword && next === current) return 'The new password must be different from the current one';
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setDone('');

    const problem = localProblem();
    if (problem) { setError(problem); return; }

    setBusy(true);
    try {
      const res = await apiFetch('/api/tracker/password', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ currentPassword: current, newPassword: next }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Could not change the password');

      setCurrent(''); setNext(''); setConfirm('');
      setDone(
        (data.wasSet ? 'Password set. ' : 'Password changed. ')
        + (data.revokedSessions > 0
            ? `${data.revokedSessions} other browser${data.revokedSessions === 1 ? ' was' : 's were'} signed out.`
            : 'You are still signed in here.')
      );
      if (data.account) onChanged(data.account);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Wrap
      title={hasPassword ? 'Password' : 'Set a password'}
      badge={hasPassword ? null : { text: 'Steam only', tone: 'muted' }}
    >
      {!hasPassword && (
        <Note zone={zone} tone="muted">
          This account signs in through Steam. Setting a password is optional, and gives
          you a second way in if you ever lose access to the Steam account.
        </Note>
      )}

      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
        {hasPassword && (
          <Field zone={zone} label="Current password" value={current} onChange={setCurrent}
                 autoComplete="current-password" />
        )}
        <Field zone={zone} label="New password" value={next} onChange={setNext}
               autoComplete="new-password" />
        <Field zone={zone} label="Confirm new password" value={confirm} onChange={setConfirm}
               autoComplete="new-password" />

        <p style={{ margin: 0, fontSize: 14.5, color: 'var(--text-muted, #9ca3af)' }}>
          At least 10 characters, with an uppercase letter and a number.
        </p>

        {error && <Note zone={zone} tone="bad">{error}</Note>}
        {done  && <Note zone={zone} tone="good">{done}</Note>}

        <div style={{ marginTop: 4 }}>
          <Action zone={zone} primary disabled={busy} type="submit">
            {busy ? 'Saving…' : hasPassword ? 'Change password' : 'Set password'}
          </Action>
        </div>
      </form>
    </Wrap>
  );
}

/** A labelled password input, in whichever skin is active. */
function Field({ zone, label, value, onChange, autoComplete }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <span style={{ fontSize: 14.5, color: 'var(--text-muted, #9ca3af)' }}>{label}</span>
      <input
        type="password"
        value={value}
        autoComplete={autoComplete}
        onChange={e => onChange(e.target.value)}
        className={zone ? 'zn-input' : undefined}
        style={zone ? { maxWidth: 320 } : {
          maxWidth: 320, padding: '7px 10px', borderRadius: 7, fontSize: 15,
          background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)',
          color: '#e5e7eb', outline: 'none',
        }}
      />
    </label>
  );
}

// ── Shells ───────────────────────────────────────────────────
// Two skins, one content. The app switches between a classic dark panel and the
// Zone board's flat blocks, and every section here renders identically in both.

function ClassicPanel({ title, badge, children }) {
  return (
    <div style={{
      background: 'var(--panel, #0b1220)',
      border: '1px solid var(--border, rgba(255,255,255,0.08))',
      borderRadius: 10, padding: '18px 20px',
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <span style={{
          fontSize: 15.5, fontWeight: 700, color: 'var(--text, #e5e7eb)',
          letterSpacing: '0.04em', textTransform: 'uppercase',
        }}>
          {title}
        </span>
        {badge && (
          <span style={{
            fontSize: 14.5, fontWeight: 600, padding: '2px 9px', borderRadius: 100,
            background: TONE[badge.tone].bg, color: TONE[badge.tone].fg,
          }}>
            {badge.text}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

function ZonePanel({ title, badge, children }) {
  return (
    <div className="zn-panel">
      <div className="zn-panel-head">
        <div className="zn-panel-h">{title}</div>
        {badge && (
          <div className="zn-panel-tools">
            <span className="zn-sticker" style={{ background: TONE[badge.tone].zn, boxShadow: '3px 3px 0 #131313' }}>
              {badge.text}
            </span>
          </div>
        )}
      </div>
      <div className="zn-panel-body">{children}</div>
    </div>
  );
}

const TONE = {
  good:  { bg: 'rgba(74,222,128,0.12)',  fg: '#4ade80', zn: '#4ade80' },
  warn:  { bg: 'rgba(251,191,36,0.12)',  fg: '#fbbf24', zn: '#fbbf24' },
  bad:   { bg: 'rgba(248,113,113,0.12)', fg: '#f87171', zn: '#f87171' },
  muted: { bg: 'rgba(255,255,255,0.06)', fg: '#9ca3af', zn: '#d4d4d4' },
};

/** A label/value grid. Values that are IDs get monospaced so they can be read digit by digit. */
function Facts({ rows, zone }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 20px',
      background: zone ? 'rgba(0,0,0,0.06)' : 'rgba(0,0,0,0.2)',
      borderRadius: 8, padding: '10px 14px', fontSize: 15,
    }}>
      {rows.map(([label, value]) => (
        <React.Fragment key={label}>
          <span style={{ color: 'var(--text-muted, #9ca3af)' }}>{label}</span>
          <span style={{
            fontWeight: 600,
            fontFamily: /^\d{17}$/.test(value) ? 'monospace' : undefined,
          }}>
            {value}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
}

function Note({ zone, tone, children }) {
  if (zone) {
    const cls = tone === 'bad' ? 'bad' : tone === 'good' ? 'good' : '';
    return <p className={`zn-ext-msg ${cls}`}>{children}</p>;
  }
  return (
    <div style={{
      marginTop: 12, borderRadius: 8, padding: '10px 14px',
      background: TONE[tone].bg,
      border: `1px solid ${TONE[tone].fg}40`,
      fontSize: 15, color: TONE[tone].fg, lineHeight: 1.5,
    }}>
      {children}
    </div>
  );
}

/** A button, or a link that looks like one. Both skins, one call site. */
function Action({ zone, href, primary, onClick, disabled, type = 'button', children }) {
  if (zone) {
    // zn-btn is written for <button>, so it carries no display or
    // text-decoration; set both here rather than widening the shared rule.
    const cls = `zn-btn${primary ? ' primary' : ''}`;
    return href
      ? <a className={cls} href={href} target="_blank" rel="noreferrer noopener"
           style={{ display: 'inline-block', textDecoration: 'none' }}>{children}</a>
      : <button type={type} className={cls} onClick={onClick} disabled={disabled}>{children}</button>;
  }

  const style = {
    padding: '7px 14px', borderRadius: 7, fontSize: 15, fontWeight: 600,
    cursor: disabled ? 'default' : 'pointer', textDecoration: 'none',
    display: 'inline-block', opacity: disabled ? 0.5 : 1,
    ...(primary
      ? { background: 'var(--indigo, #818cf8)', color: '#fff', border: 'none' }
      : { background: 'rgba(255,255,255,0.05)', color: 'var(--text-muted, #9ca3af)',
          border: '1px solid rgba(255,255,255,0.08)' }),
  };

  return href
    ? <a href={href} target="_blank" rel="noreferrer noopener" style={style}>{children}</a>
    : <button type={type} onClick={onClick} disabled={disabled} style={style}>{children}</button>;
}
